import type { DisplayColumnDef } from "@tanstack/vue-table";
import { h } from "vue";

import type { GenericModel } from "@/types/model";

import ActionColumn from "./components/ActionColumn.vue";
import RowSelection from "./components/RowSelection.vue";
import type { ActionItem } from "./types/overflow";

interface SelectionColumnOptions<T extends GenericModel> {
  label: (context: { item: T; index: number }) => string;
  showOnMobile?: boolean;
}

export const SELECTION_COLUMN_ID = "$$selection";
export const ACTIONS_COLUMN_ID = "$$actions";

export const defineSelectionColumn = <T extends GenericModel>({
  label,
  showOnMobile = false,
}: SelectionColumnOptions<T>): DisplayColumnDef<T> => {
  return {
    id: SELECTION_COLUMN_ID,
    enableHiding: false,
    enableSorting: false,
    cell({ row, table }) {
      const selection = table.options.meta?.selection;
      if (!selection) {
        return;
      }

      return h(RowSelection, {
        table: table as any,
        item: row.original,
        selection: selection as any,
        label: () => label({ item: row.original, index: row.index }),
      });
    },
    meta: {
      showOnMobile,
      cellClass: `select-none`,
    },
  };
};

interface ActionsColumnsOptions<T extends GenericModel> {
  actions: (context: { item: T; index: number }) => ActionItem[];
}

export const defineActionsColumn = <T extends GenericModel>(
  options: ActionsColumnsOptions<T>
): DisplayColumnDef<T> => {
  return {
    id: ACTIONS_COLUMN_ID,
    enableHiding: false,
    enableSorting: false,
    meta: {
      fixed: "end",
      showOnMobile: true,
      cellClass: "lg:px-4",
      actionsRegistrar: options.actions,
    },
    cell({ row }) {
      return h(ActionColumn, {
        registrar: () =>
          options.actions({ item: row.original, index: row.index }),
      });
    },
  };
};
